<template>
  <div class="details">
    <!-- tabs/ right column -->
    <div class="tabs">
      <div
        class="tab font-20 weight-600"
        :class="{ active: selectedTab === 'details' }"
        @click="selectTab('details')"
        data-test="details-tab"
      >
        Fundraiser Details
      </div>
      <div
        class="tab font-20 weight-600"
        :class="{ active: selectedTab === 'donations' }"
        @click="selectTab('donations')"
        data-test="donations-tab"
      >
        Donations
      </div>
    </div>
    <div>
      <!-- details tab -->
      <div
        v-if="selectedTab === 'details'"
        class="tabs-holder"
        data-test="details-container"
      >
        <div class="weight-400 font-14">Benefitting Organization</div>
        <div
          class="weight-600 font-16"
          style="padding-bottom: 16px"
        >
          {{ fundraiser.associatedOrgName }}
        </div>
        <div
          v-if="fundraiser.companyGiving"
          class="weight-400 font-14"
        >
          Affiliated Company
        </div>
        <div
          v-if="fundraiser.companyGiving"
          class="weight-600 font-16"
          style="padding-bottom: 16px"
        >
          {{ fundraiser.companyGiving.name }}
        </div>
        <div class="weight-400 font-14 flex">
          <span>Fundraiser URL</span>
        </div>
        <div
          class="weight-600 font-16"
          @click="copyDefaultValue"
          style="
            padding-bottom: 16px;
            overflow-wrap: break-word;
            white-space: normal;
            overflow: hidden;
            text-overflow: ellipsis;
          "
        >
          {{ fullUrl }}
          <Copy
            :fill="mainColor || '#282968'"
            style="cursor: pointer; display: inline-block"
            data-cy="copy-button"
            data-test="copy-button"
          />
        </div>
        <div
          v-if="fundraiser.associatedGivesDay"
          class="weight-400 font-14"
        >
          Gives Day Event
        </div>
        <div
          v-if="fundraiser.associatedGivesDay"
          class="weight-600 font-16"
          style="padding-bottom: 16px"
        >
          {{ fundraiser.associatedGivesDay }}
        </div>
        <div class="weight-400 font-14">Created On</div>
        <div
          class="weight-600 font-16"
          style="padding-bottom: 16px"
        >
          {{ filters.formatDate(fundraiser.enteredDate) }}
        </div>
        <div class="weight-400 font-14" v-if="fundraiser.endDate">End Date</div>
        <div class="weight-600 font-16" v-if="fundraiser.endDate">{{ filters.formatDate(fundraiser.endDate) }}</div>
      </div>
      <!-- donations tab -->
      <div
        v-if="selectedTab === 'donations'"
        class="donations-container"
        data-test="donations-container"
      >
        <div
          v-if="fundraiserStore.fundraiserDonations.length == 0"
          class="no-donations"
          data-test="no-donations"
        >
          <span class="weight-600 font-20">No Donations Yet</span
          ><span
            class="weight-400 font-16"
            style="margin-top: 20px; text-align: center"
            >Donations will populate as people contribute to your fundraiser</span
          >
        </div>
        <div v-else>
          <Button
            class="p-button-outlined p-button-sm"
            :disabled="disableExport"
            @click="exportDonations"
            style="display: flex; justify-content: flex-end"
            data-test="export-button"
          >
            Export
            <img
              style="padding-right: 5px"
              :src="Export"
              :class="{ 'disabled-img': disableExport }"
            />
          </Button>
          <div
            v-for="(donation, index) in visibleDonations"
            :key="`donation_${index}`"
            class="recent-donation-tile"
          >
            <div class="donations-top">
              <div class="donation-date">
                {{ filters.formatDate(donation.enteredDate) }}
              </div>
            </div>
            <div
              class="flex"
              style="align-items: center"
            >
              <div class="donation-amount">
                {{ donationAmount(donation) }}
              </div>
              <div class="donation-cause">
                From <span class="weight-500">{{ getDonorName(donation) }}</span>
              </div>
            </div>
          </div>
          <div
            v-if="!visibleDonations.length == 0"
            class="pagination"
          >
            <Button
              class="p-button-text p-button-sm weight-900"
              @click="prevPage"
              v-if="page !== 1"
            >
              <img
                class="icon"
                :src="PreviousArrow"
                alt="previous-page"
              />Previous Page
            </Button>
            <div
              v-if="!isMobile"
              v-for="(pageNumber, index) in displayedPages"
              :key="index"
              style="cursor: pointer"
            >
              <div
                class="pageNumber"
                @click="selectPage(pageNumber)"
                v-if="pageNumber === '...'"
              >
                {{ pageNumber }}
              </div>
              <div
                @click="selectPage(pageNumber)"
                v-else
                class="pageNumber weight-700"
                :style="{
                  backgroundColor: currentPage === pageNumber ? donorStore.mainColor : 'transparent',
                  borderRadius: currentPage === pageNumber ? '50%' : '0',
                }"
              >
                {{ pageNumber }}
              </div>
            </div>
            <Button
              class="p-button-text p-button-sm weight-900"
              @click="nextPage"
              v-if="currentPage !== totalPages"
            >
              Next Page<img
                class="icon"
                :src="NextArrow"
                alt="next-page"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onBeforeMount, watch } from 'vue';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';
import { useRoute } from 'vue-router';
import filters from '@/helpers/filters';
import Export from '@/assets/Export.svg';
import PreviousArrow from '@/assets/PreviousArrow.svg';
import NextArrow from '@/assets/NextArrow.svg';
import Copy from '@/assets/Copy.vue';
import { useSnackbarStore } from '@/stores/SnackbarStore.js';
import useIsMobile from '@/composables/useIsMobile.js';
import { exportDonations as exportDonationsHelper } from '@/helpers/exportHelper.js';

const donorStore = useDonorStore();
const fundraiserStore = useFundraiserStore();
const snackbarStore = useSnackbarStore();
const { isMobile } = useIsMobile();

const route = useRoute();

const selectedTab = ref('details');

const page = ref(1);
const itemsPerPage = ref(5);
const visibleDonations = ref([]);
const fundraiserId = parseInt(route.params.idOrName, 10);
const fundraiser = donorStore.getFundraiserById(fundraiserId);
const mainColor = donorStore.mainColor;
const fullUrl = fundraiser.domain.replace(/\/$/, '') + '/fundraiser/' + fundraiser.urlLink;

// details tabs
function selectTab(tab) {
  selectedTab.value = tab;
}

async function exportDonations() {
  const result = await exportDonationsHelper(fundraiserStore, fundraiserStore.fundraiserDonations);
  if (result === false) {
    useSnackbarStore().show('Failed to export donations.', 'error');
  }
}

//donation list tab
const getDonorName = (donation) => {
  if (donation.anonymous == 'Not Anonymous' || donation.anonymous == 'Amount Not Displayed Publicly') {
    return donation.payment.billingFirstName + ' ' + donation.payment.billingLastName?.charAt(0).toUpperCase();
  } else if (donation.anonymous == 'Name Not Displayed Publicly') {
    return 'Anonymous Donor';
  }
};

const donationAmount = (donation) => {
  let amount = donation.anonymous != 'Amount Not Displayed Publicly' ? filters.formatMoney(donation.amount) : '';
  return amount;
};

// pagination for donations
const loadData = () => {
  filterOptions();
};

const filterOptions = () => {
  const start = (page.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  visibleDonations.value = fundraiserStore.fundraiserDonations.slice(start, end);
};

const totalPages = computed(() => {
  return Math.ceil(fundraiserStore.fundraiserDonations.length / itemsPerPage.value);
});

const selectPage = (pageNumber) => {
  if (pageNumber === '...') {
    return;
  }
  page.value = pageNumber;
  loadData();
};

const currentPage = computed(() => {
  return page.value;
});

const displayedPages = computed(() => {
  const result = [];
  const totalPages = Math.ceil(fundraiserStore.fundraiserDonations.length / itemsPerPage.value);
  const currentPage = page.value;

  // Show ellipsis and 3 pages before and after the current page
  for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
    result.push(i);
  }

  // Add ellipsis if there are more pages before the current page
  if (result[0] > 1) {
    result.unshift('...');
  }

  // Add ellipsis if there are more pages after the current page
  if (result[result.length - 1] < totalPages) {
    result.push('...');
  }

  return result;
});

const prevPage = () => {
  if (page.value > 1) {
    page.value--;
    loadData();
  }
};

const nextPage = () => {
  const lastPage = Math.ceil(fundraiserStore.fundraiserDonations.length / itemsPerPage.value);
  if (page.value < lastPage) {
    page.value++;
    loadData();
  }
};

watch(totalPages, () => {
  page.value = 1;
});

// export button
const disableExport = computed(() => {
  return fundraiserStore.fundraiserDonations.length == 0;
});

// copy fundraiser url
const copyDefaultValue = () => {
  navigator.clipboard.writeText(fullUrl);
  // Show a toast notification to the user that the link was copied
  snackbarStore.show('Link Copied!', 'info');
};

onBeforeMount(async () => {
  if (fundraiser) {
    await fundraiserStore.fetchFundraiserDonations(donorStore.donorInfo.donorAccounts_id, fundraiser.fundraisers_id);
    if (fundraiser.givesDays_id) {
      await donorStore.getFundraiserDonationData(fundraiser.givesDays_id);
    }
    loadData();
  }
});
</script>
<style lang="scss" scoped>
.details {
  width: 560px;
  padding: 24px 60px 40px 60px;
}

.tabs {
  width: 561px;
  margin: 0;
  display: flex;
  justify-content: center;
  gap: 20px;
}
.tabs-holder {
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  background: #ffffff;
  width: 100%;
  padding: 24px 60px 40px 60px;
  width: 561px;
}
.tab {
  cursor: pointer;
  margin: 10px;
}
.tab:hover {
  padding-bottom: 5px;
  border-bottom: 3px solid;
  border-bottom-color: var(--p-primary-500);
  margin-bottom: 20px;
}
.tab.active {
  padding-bottom: 5px;
  border-bottom: 3px solid;
  border-bottom-color: var(--p-primary-500);
  margin-bottom: 20px;
}

.pageNumber {
  padding: 10px;
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination .pageNumber {
  margin: 0 5px;
  padding: 0 8px;
  border-radius: 3px;
  cursor: pointer;
}
.pagination .pageNumber:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.recent-donations {
  width: 334px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.recent-donation-tile {
  background-color: #ffffff;
  border-radius: 10px;
  padding: 12px 20px 16px 20px;
  margin: 8px 0;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.12);
  width: 100%;
}

.no-donations {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
}

.donations-container {
  width: 561px;
}
.donations-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 17px;
}
.donation-date {
  font-size: 0.75rem;
  font-weight: 400;
}
.donation-amount {
  font-size: 1.3em;
  font-weight: 600;
}
.donation-cause {
  font-size: 0.875em;
  font-weight: 400;
  padding-left: 12px;
}

.copy-url {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 8px;
  font-size: 0.875em;
  font-weight: 400;
  color: #000000;
  text-decoration-line: underline;
  margin-bottom: 8px;
  cursor: pointer;
}

@media (max-width: 1288px) {
  .details {
    padding: 0;
  }
}

@media (max-width: 768px) {
  .details {
    padding-bottom: 80px;
  }
  .tabs {
    width: 325px;
    flex-wrap: wrap;
  }
  .donations-container {
    width: 335px;
  }
  .tabs-holder {
    width: 335px;
  }
  .details {
    width: 335px;
  }
}
</style>
